<app-page-breadcrumb pageTitle="Roles y permisos"></app-page-breadcrumb>

<app-component-card>
  <!-- Toolbar -->
  <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          Buscar
        </label>
        <input
          type="text"
          [(ngModel)]="filtroSearch"
          (ngModelChange)="aplicarFiltros()"
          placeholder="Nombre o descripción..."
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          Tipo
        </label>
        <select
          [(ngModel)]="filtroTipo"
          (ngModelChange)="aplicarFiltros()"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">Todos</option>
          <option value="system">De sistema</option>
          <option value="custom">Personalizados</option>
        </select>
      </div>
    </div>
    <ng-container *appHasPermission="'roles:create'">
      <app-button variant="primary" (btnClick)="nuevo()">+ Nuevo rol</app-button>
    </ng-container>
  </div>

  <!-- Los roles de sistema son protegidos: traen una matriz predefinida y
       diferenciada (V191) y solo pueden consultarse. INC_CRM_024 inc01. -->
  <p class="mb-4 text-xs text-gray-500 dark:text-gray-400">
    Los roles <strong>de sistema</strong> traen una matriz de permisos predefinida y
    diferenciada por responsabilidad; se pueden consultar con «Ver matriz» pero no
    modificar. Para un alcance a medida, cree un rol <strong>personalizado</strong>.
  </p>

  @if (error) {
    <p class="mb-3 text-sm text-error-600 dark:text-error-400">{{ error }}</p>
  }

  @if (loading) {
    <div class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
    </div>
  } @else if (filtered.length === 0) {
    <p class="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
      No hay roles registrados.
    </p>
  } @else {
    <div class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Nombre</th>
            <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Descripción</th>
            <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 w-28">Tipo</th>
            <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 w-44">Permisos</th>
            <th class="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400 w-52">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          @for (role of filtered; track trackById($index, role)) {
            <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">{{ role.name }}</td>
              <td class="px-3 py-2 text-gray-600 dark:text-gray-400">
                {{ role.description || '—' }}
              </td>
              <td class="px-3 py-2">
                @if (role.isSystem) {
                  <app-badge color="info">Sistema</app-badge>
                } @else {
                  <app-badge color="primary">Personalizado</app-badge>
                }
              </td>
              <td class="px-3 py-2 text-gray-600 dark:text-gray-400">
                <span class="block">{{ permissionsSummary(role) }}</span>
                <span class="block text-xs text-gray-400 dark:text-gray-500">
                  {{ actionsSummary(role) }}
                </span>
              </td>
              <td class="px-3 py-2">
                <div class="flex items-center justify-end gap-1">
                  <!-- Ver matriz. En los roles de sistema (no editables) es la única
                       forma de consultar sus permisos, así que va con etiqueta visible. -->
                  <button
                    type="button"
                    (click)="ver(role)"
                    title="Ver matriz de permisos"
                    class="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-brand-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Ver matriz
                  </button>
                  <!-- Editar (solo custom) -->
                  <button
                    type="button"
                    *appHasPermission="'roles:edit'"
                    (click)="editar(role)"
                    [disabled]="role.isSystem"
                    [title]="role.isSystem ? 'Los roles de sistema no se pueden editar' : 'Editar'"
                    class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <!-- Eliminar (solo custom) -->
                  <button
                    type="button"
                    *appHasPermission="'roles:delete'"
                    (click)="eliminar(role)"
                    [disabled]="role.isSystem"
                    [title]="role.isSystem ? 'Los roles de sistema no se pueden eliminar' : 'Eliminar'"
                    class="p-1.5 rounded-lg text-gray-500 hover:bg-error-50 hover:text-error-600 dark:text-gray-400 dark:hover:bg-error-500/10 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</app-component-card>
